import type { Freshness, Lead, LeadDirection } from "./types";
import { normalizeGpuHardware } from "./hardware";

export const directionLabels: Record<LeadDirection, string> = {
  sell: "出售",
  buy: "求购",
};

export const statusLabels: Record<Lead["status"], string> = {
  draft: "草稿",
  active: "有效",
  locked: "已锁定",
  closed: "已关闭",
  done: "已成交",
  archived: "已归档",
};

export const availabilityLabels: Record<Lead["availability"], string> = {
  stock: "现货",
  transit: "在途",
  presale: "预售",
  unknown: "待确认",
};

export const freshnessLabels: Record<Freshness, string> = {
  fresh: "新鲜",
  expiring: "待核验",
  expired: "已过期",
  archived: "已归档",
};

export function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

export function fromLocalInput(value: string): string | null {
  return value ? new Date(value).toISOString() : null;
}

export function defaultValidUntil(direction: LeadDirection): string {
  return new Date(
    Date.now() + (direction === "sell" ? 48 : 72) * 3_600_000,
  ).toISOString();
}

export function createEmptyLead(
  direction: LeadDirection = "sell",
  owner = "我",
): Lead {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    direction,
    status: "active",
    category: "算力设备",
    brand: "",
    sku: "",
    model: "",
    configuration: "",
    memory_capacity: "",
    quantity: null,
    unit: "台",
    price: null,
    currency: "CNY",
    estimated_fee_per_unit: null,
    price_basis: "未税自提",
    availability: "stock",
    location: "",
    available_at: null,
    valid_until: defaultValidUntil(direction),
    last_verified_at: now,
    payment_terms: "",
    inspection: "",
    transaction_steps: [],
    company: "",
    contact_name: "",
    contact_id: null,
    contact_avatar: "",
    contact_info: "",
    source_channel: "手工录入",
    raw_text: "",
    owner,
    notes: "",
    note_images: [],
    ai_confidence: null,
    created_by: owner,
    created_at: now,
    updated_at: now,
    deleted_at: null,
  };
}

export function normalizeLeadRecord(input: Partial<Lead>): Lead {
  const base = createEmptyLead(
    input.direction === "buy" ? "buy" : "sell",
    input.owner || "我",
  );
  const rawFee = input.estimated_fee_per_unit as
    number | string | null | undefined;
  const fee = Number(rawFee);
  const record: Lead = {
    ...base,
    ...input,
    sku: String(input.sku ?? ""),
    memory_capacity: String(input.memory_capacity ?? ""),
    contact_id:
      typeof input.contact_id === "string" && input.contact_id
        ? input.contact_id
        : null,
    contact_avatar:
      typeof input.contact_avatar === "string" &&
      input.contact_avatar.startsWith("data:image/")
        ? input.contact_avatar
        : "",
    estimated_fee_per_unit:
      rawFee === null || rawFee === undefined || rawFee === ""
        ? null
        : Number.isFinite(fee)
          ? fee
          : null,
    transaction_steps: Array.isArray(input.transaction_steps)
      ? input.transaction_steps
          .map(String)
          .map((step) => step.trim())
          .filter(Boolean)
      : [],
    note_images: Array.isArray(input.note_images)
      ? input.note_images.filter(
          (image): image is string =>
            typeof image === "string" && image.startsWith("data:image/"),
        )
      : [],
  };
  return { ...record, ...normalizeGpuHardware(record) };
}

export function getFreshness(lead: Lead, now = Date.now()): Freshness {
  if (["closed", "done", "archived"].includes(lead.status)) return "archived";
  const validUntil = new Date(lead.valid_until).getTime();
  if (Number.isFinite(validUntil) && validUntil <= now) return "expired";
  const verified = new Date(lead.last_verified_at).getTime();
  if (
    (Number.isFinite(validUntil) && validUntil - now < 24 * 3_600_000) ||
    (Number.isFinite(verified) && now - verified > 24 * 3_600_000)
  ) {
    return "expiring";
  }
  return "fresh";
}

export function formatPrice(lead: Lead): string {
  if (lead.price === null || lead.price === undefined) return "价格面议";
  const value = new Intl.NumberFormat("zh-CN", {
    maximumFractionDigits: 2,
  }).format(lead.price);
  return `${value} ${lead.currency} / ${lead.unit}`;
}

export function formatCompactPrice(lead: Lead): string {
  if (lead.price === null || lead.price === undefined) return "价格面议";
  if (Math.abs(lead.price) >= 10_000) {
    const value = new Intl.NumberFormat("zh-CN", {
      maximumFractionDigits: 2,
    }).format(lead.price / 10_000);
    return `${value} 万 ${lead.currency} / ${lead.unit}`;
  }
  return formatPrice(lead);
}

export function calculateTotalUnitPrice(lead: Lead): number | null {
  if (lead.price === null || lead.estimated_fee_per_unit === null) return null;
  return lead.price + lead.estimated_fee_per_unit;
}

export function formatCompactAmount(value: number, currency: string): string {
  const abs = Math.abs(value);
  const tier =
    abs >= 100_000_000
      ? { divisor: 100_000_000, suffix: " 亿" }
      : abs >= 10_000
        ? { divisor: 10_000, suffix: " 万" }
        : { divisor: 1, suffix: "" };
  const formatted = new Intl.NumberFormat("zh-CN", {
    maximumFractionDigits: 2,
  }).format(value / tier.divisor);
  return `${formatted}${tier.suffix} ${currency}`;
}

export function formatTotalUnitPrice(lead: Lead): string {
  const total = calculateTotalUnitPrice(lead);
  return total === null ? "—" : formatCompactAmount(total, lead.currency);
}

export function formatQuantity(lead: Lead): string {
  return lead.quantity === null || lead.quantity === undefined
    ? "数量待确认"
    : `${new Intl.NumberFormat("zh-CN", { maximumFractionDigits: 2 }).format(lead.quantity)} ${lead.unit}`;
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return "待确认";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "待确认";
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

export function relativeTime(iso: string, now = Date.now()): string {
  const diff = now - new Date(iso).getTime();
  if (!Number.isFinite(diff)) return "未知";
  if (Math.abs(diff) < 60_000) return "刚刚";
  if (Math.abs(diff) < 3_600_000)
    return `${Math.floor(Math.abs(diff) / 60_000)} 分钟${diff >= 0 ? "前" : "后"}`;
  if (Math.abs(diff) < 86_400_000)
    return `${Math.floor(Math.abs(diff) / 3_600_000)} 小时${diff >= 0 ? "前" : "后"}`;
  return `${Math.floor(Math.abs(diff) / 86_400_000)} 天${diff >= 0 ? "前" : "后"}`;
}

export function leadSearchText(lead: Lead): string {
  return [
    lead.model,
    lead.brand,
    lead.sku,
    lead.memory_capacity,
    lead.category,
    lead.configuration,
    lead.company,
    lead.contact_name,
    lead.location,
    lead.notes,
    lead.raw_text,
    ...lead.transaction_steps,
  ]
    .join(" ")
    .toLowerCase();
}

export function exportLeadsCsv(leads: Lead[]): void {
  const columns: Array<[keyof Lead, string]> = [
    ["direction", "方向"],
    ["status", "状态"],
    ["category", "品类"],
    ["brand", "品牌"],
    ["sku", "SKU"],
    ["model", "型号规格"],
    ["configuration", "配置"],
    ["memory_capacity", "内存容量"],
    ["quantity", "数量"],
    ["unit", "单位"],
    ["price", "单价"],
    ["currency", "币种"],
    ["estimated_fee_per_unit", "预估居间费/台"],
    ["price_basis", "价格口径"],
    ["availability", "库存状态"],
    ["location", "交割地"],
    ["available_at", "可交割时间"],
    ["valid_until", "有效至"],
    ["last_verified_at", "最近核验"],
    ["payment_terms", "付款条件"],
    ["inspection", "验货要求"],
    ["transaction_steps", "交易流程"],
    ["company", "公司"],
    ["contact_name", "联系人"],
    ["contact_info", "联系方式"],
    ["source_channel", "来源"],
    ["owner", "负责人"],
    ["notes", "备注"],
    ["raw_text", "原始消息"],
    ["updated_at", "更新时间"],
  ];
  const escape = (value: unknown) =>
    `"${String(value ?? "").replaceAll('"', '""')}"`;
  const rows = [
    columns.map(([, label]) => escape(label)).join(","),
    ...leads.map((lead) =>
      columns
        .map(([key]) => {
          const value = lead[key];
          if (key === "direction")
            return escape(directionLabels[lead.direction]);
          if (key === "status") return escape(statusLabels[lead.status]);
          if (key === "availability")
            return escape(availabilityLabels[lead.availability]);
          return escape(value as string | number | null);
        })
        .join(","),
    ),
  ];
  const blob = new Blob(["\ufeff", rows.join("\r\n")], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `货盘-${new Date().toISOString().slice(0, 10)}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}

function seed(partial: Partial<Lead>, direction: LeadDirection = "sell"): Lead {
  return { ...createEmptyLead(direction), ...partial };
}

export function createSeedLeads(): Lead[] {
  const now = Date.now();
  const iso = (offsetHours: number) =>
    new Date(now + offsetHours * 3_600_000).toISOString();
  return [
    seed({
      model: "B300 3T",
      brand: "超微",
      sku: "B300",
      memory_capacity: "3T",
      configuration: "8U 风冷整机",
      quantity: 64,
      price: 256,
      currency: "USDT",
      estimated_fee_per_unit: 2,
      location: "香港",
      valid_until: iso(18),
      last_verified_at: iso(-1),
      company: "港算供应链",
      contact_name: "邱哥",
      contact_info: "+852 6*** 2188",
      price_basis: "未税仓自提",
      payment_terms: "现场验货后分批结算",
      inspection: "核对 SN，支持 NCCL 压测",
      transaction_steps: [
        "验资",
        "现场验货并核对 SN",
        "验一付一",
        "香港仓提货",
      ],
      owner: "阿哲",
      source_channel: "微信",
      raw_text:
        "邱哥：香港现货，超微 B300 3T，64 台，256 USDT / 台，预估居间费 2 USDT / 台。",
    }),
    seed(
      {
        model: "HGX B300 8 卡整机",
        brand: "Dell / Supermicro",
        configuration: "风冷优先，液冷需注明机房条件",
        quantity: 20,
        price: null,
        location: "深圳 / 香港",
        valid_until: iso(52),
        last_verified_at: iso(-3),
        company: "南岸智算",
        contact_name: "林总",
        contact_info: "微信：lin***88",
        price_basis: "预算内议价",
        availability: "unknown",
        payment_terms: "验货后公对公",
        inspection: "需提供近期实拍及 SN 清单",
        owner: "我",
        source_channel: "WhatsApp",
        raw_text: "终端求购 20 台 B300 整机，深圳或香港交付均可，本周有效。",
      },
      "buy",
    ),
    seed({
      model: "DGX B300 8 卡整机",
      brand: "NVIDIA",
      configuration: "原厂整机 · 配置以 SN 清单为准",
      quantity: 2,
      price: 1_060_000,
      currency: "USD",
      location: "香港 · 荃湾",
      valid_until: iso(8),
      last_verified_at: iso(-26),
      company: "汇桥科技",
      contact_name: "Mandy",
      contact_info: "m***@example.com",
      price_basis: "香港仓自提",
      status: "locked",
      owner: "小周",
      source_channel: "邮件",
    }),
    seed({
      model: "电解铜 A 级",
      brand: "",
      category: "有色金属",
      configuration: "Cu-CATH-1",
      quantity: 500,
      unit: "吨",
      price: 78_200,
      location: "上海 · 宝山",
      valid_until: iso(30),
      last_verified_at: iso(-2),
      company: "东澜金属",
      contact_name: "吴经理",
      contact_info: "138 **** 6621",
      price_basis: "含税出库",
    }),
    seed({
      model: "HGX B300 8 卡整机",
      brand: "Supermicro",
      configuration: "4U 液冷",
      quantity: 4,
      price: 7_150_000,
      location: "香港 · 元朗",
      valid_until: iso(-4),
      last_verified_at: iso(-30),
      company: "西环设备",
      contact_name: "何生",
      contact_info: "+852 5*** 0932",
      owner: "阿哲",
      source_channel: "微信",
    }),
    seed(
      {
        model: "HGX B300 8 卡整机",
        brand: "不限",
        configuration: "终端自用，要求风冷",
        quantity: 8,
        price: 7_600_000,
        location: "深圳",
        valid_until: iso(-12),
        last_verified_at: iso(-36),
        company: "北辰量化",
        contact_name: "周工",
        contact_info: "微信：z***91",
        status: "done",
        owner: "我",
      },
      "buy",
    ),
  ];
}
